import { FC, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import ButtonBase from 'src/components/Button';
import Image from 'src/components/Image';
import ModalLayoutBase, { MobileModalType, ModalFooter as ModalFooterBase, ModalTitleDescription } from 'src/components/ModalLayout';
import ResizingText from 'src/components/ResizingText';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import STTConsentLeaveModal from './STTConsentLeaveModal';
import { setSttPermissionAcknowledged } from './utils';
interface SpeechRecognitionPermissionModalProps {
  referrer?: string;
  onComplete?: () => void;
}
const ModalLayout = styled(ModalLayoutBase)`
  width: 400px;
`;
const Header = styled.div`
  z-index: 1;
  padding: 20px 24px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;
`;
const TitleRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
`;
const ModalTitle = styled(ResizingText)`
  flex: 1;
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  color: ${({
  theme
}) => theme.color.content12};
`;
const SkipButton = styled.button`
  all: unset;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content8};
  text-align: end;
  white-space: nowrap;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -7px -8px;
    min-width: 40px;
    min-height: 40px;
  }
`;
const Description = styled(ModalTitleDescription)`
  font-size: 14px;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content9};
`;
const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${({
  theme
}) => theme.color.bg2};
`;
const OnboardingImage = styled(Image)`
  width: 124px;
  height: 124px;
  object-fit: contain;
`;
const ModalFooter = styled(ModalFooterBase)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Button = styled(ButtonBase)`
  width: 100%;
`;

/**
 * 시스템 권한이 불필요한 브라우저인 경우, 형식상의 STT 권한 요청 모달
 */
const SpeechRecognitionPermissionModal: FC<SpeechRecognitionPermissionModalProps> = ({
  referrer,
  onComplete
}) => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const showModal = useSetAtom(showModalAtom);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.ANYWHERE__IMPRESSION_STT_CONSENT_POPUP,
      eventParams: {
        action_category: 'impression',
        tab: 'anywhere',
        page: 'anywhere',
        target: 'stt_consent_popup',
        referrer
      }
    });
  }, [referrer]);
  const handleDone = useCallback(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.STT_CONSENT_POPUP__CLICK_AGREE,
      eventParams: {
        action_category: 'click',
        tab: 'anywhere',
        page: 'stt_consent_popup',
        target: 'agree',
        referrer
      }
    });
    setSttPermissionAcknowledged(true);
    closeModal(ModalType.SPEECH_RECOGNITION_PERMISSION);
    onComplete?.();
  }, [closeModal, onComplete, referrer]);
  const handleSkip = useCallback(() => {
    showModal({
      key: ModalType.STT_CONSENT_LEAVE,
      component: () => <STTConsentLeaveModal onBack={() => {
        closeModal(ModalType.STT_CONSENT_LEAVE);
      }} onLeave={() => {
        setSttPermissionAcknowledged(false);
        closeModal(ModalType.STT_CONSENT_LEAVE);
        closeModal(ModalType.SPEECH_RECOGNITION_PERMISSION);
      }} />
    });
  }, [showModal, closeModal]);
  return <ModalLayout isDim mobileType={MobileModalType.BOTTOM_SHEET} closeWhenClickOutside={false} closeWithESC={false} navButtonType={ModalNavButtonType.NONE} mobileNavButtonType={ModalNavButtonType.NONE} data-testid='SpeechRecognitionPermissionModal' data-sentry-element="ModalLayout" data-sentry-component="SpeechRecognitionPermissionModal" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx">
      <Header data-sentry-element="Header" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx">
        <TitleRow data-sentry-element="TitleRow" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx">
          <ModalTitle minFontSize={14} maxLines={2} data-sentry-element="ModalTitle" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx">
            {t('azar_assistant.consent_stt_popup_title')}
          </ModalTitle>
          <SkipButton onClick={handleSkip} data-testid='SpeechRecognitionPermissionModal-button-skip' data-sentry-element="SkipButton" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx">
            {t('COMMON__SKIP')}
          </SkipButton>
        </TitleRow>
        <Description dangerouslySetInnerHTML={{
        __html: t('azar_assistant.consent_stt_popup_desc')
      }} data-sentry-element="Description" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx" />
      </Header>
      <ImageWrapper data-sentry-element="ImageWrapper" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx">
        <OnboardingImage src='/images/onboarding/imgMic.png' alt='' data-sentry-element="OnboardingImage" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx" />
      </ImageWrapper>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx">
        <Button id='speech-recognition-done' onClick={handleDone} data-testid='SpeechRecognitionPermissionModal-button-done' data-sentry-element="Button" data-sentry-source-file="SpeechRecognitionPermissionModal.tsx">
          {t('common_confirm')}
        </Button>
      </ModalFooter>
    </ModalLayout>;
};
export default SpeechRecognitionPermissionModal;